import { useCallback, useRef } from "react";

const EDGE_URL = "https://bpzsocjzlwyssgxidrkm.supabase.co/functions/v1/verify-invite-code";

const STORAGE_KEY_RATE_TIMES = "vault_rate_times";
const RATE_WINDOW_MS = 10 * 1000;
const RATE_MAX = 5;

export interface InviteVerifyResult {
  success: boolean;
  name?: string | null;
  tier?: string | null;
  reason?: "not_found" | "inactive" | "expired" | "limit_reached" | "server_error" | "rate_limited" | "invalid_input";
}

function getFingerprint(): string {
  const nav = window.navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join("|");
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return String(Math.abs(hash));
}

function checkRateLimit(): boolean {
  const now = Date.now();
  let times: number[] = [];
  try {
    times = JSON.parse(localStorage.getItem(STORAGE_KEY_RATE_TIMES) ?? "[]");
  } catch {
    times = [];
  }
  const recent = times.filter((t) => now - t < RATE_WINDOW_MS);
  recent.push(now);
  localStorage.setItem(STORAGE_KEY_RATE_TIMES, JSON.stringify(recent));
  return recent.length > RATE_MAX;
}

export function useInviteCode() {
  const pendingRef = useRef(false);

  const verifyCode = useCallback(async (code: string): Promise<InviteVerifyResult> => {
    if (pendingRef.current) {
      return { success: false, reason: "rate_limited" };
    }

    if (checkRateLimit()) {
      return { success: false, reason: "rate_limited" };
    }

    pendingRef.current = true;
    try {
      const res = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          fingerprint: getFingerprint(),
        }),
      });

      const data = await res.json();
      return data as InviteVerifyResult;
    } catch {
      return { success: false, reason: "server_error" };
    } finally {
      pendingRef.current = false;
    }
  }, []);

  return { verifyCode };
}
